import React, { useState } from 'react';

// FastAPI server URL for database connection endpoints
// Use environment variable, or window.location.origin for production, or fallback to localhost for dev
const FASTAPI_URL = process.env.REACT_APP_FASTAPI_URL || 
  (typeof window !== 'undefined' ? window.location.origin : 'http://127.0.0.1:7860');

const DatabaseConnection = ({ onConnect, onBack, session }) => {
  const [formData, setFormData] = useState({
    server: '',
    database: '',
    username: '',
    password: ''
  });
  const [connectorId, setConnectorId] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsConnecting(true);
    setError('');

    try {
      // Include session username for query logging
      const payload = {
        ...formData,
        username: session?.profile?.username || session?.username || formData.username
      };
      
      const response = await fetch(`${FASTAPI_URL}/api/connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        onConnect({
          ...formData,
          connectionId: data.connectionId
        });
      } else {
        setError('Connection issue: FlashAI couldn\'t reach the database. Please contact your IT department.');
      }
    } catch (err) {
      setError('Connection issue: FlashAI couldn\'t reach the database. Please contact your IT department.');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleConnectWithEnv = async () => {
    setIsConnecting(true);
    setError('');

    try {
      // Include session username for query logging
      const payload = {
        username: session?.profile?.username || session?.username || 'env-user'
      };
      
      const response = await fetch(`${FASTAPI_URL}/api/connect-env`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        onConnect({
          server: 'Environment Variable',
          database: data.database,
          username: 'Environment Variable',
          password: 'Environment Variable',
          connectionId: data.connectionId
        });
      } else {
        setError('Connection issue: FlashAI couldn\'t reach the database. Please contact your IT department.');
      }
    } catch (err) {
      setError('Connection issue: FlashAI couldn\'t reach the database. Please contact your IT department.');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleConnectWithConnector = async () => {
    if (!connectorId.trim()) {
      setError('Please enter a Connector ID');
      return;
    }

    setIsConnecting(true);
    setError('');

    try {
      // Include session username for query logging
      const payload = {
        connectorId: connectorId.trim(),
        username: session?.profile?.username || session?.username || 'connector-user'
      };
      
      const response = await fetch(`${FASTAPI_URL}/api/connect-connector`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        onConnect({
          server: 'Connector',
          database: data.database || 'Remote Database',
          username: 'Connector',
          password: 'Connector',
          connectionId: data.connectionId,
          connectorId: connectorId.trim()
        });
      } else {
        setError('Connection issue: FlashAI couldn\'t reach the connector. Please check your Connector ID and ensure the connector is running.');
      }
    } catch (err) {
      setError('Connection issue: FlashAI couldn\'t reach the connector. Please check your Connector ID and ensure the connector is running.');
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <div className="flex h-screen flex-col">
      <header className="flex items-center justify-between border-b border-slate-200/80 px-3 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-6 h-6 sm:w-8 sm:h-8 text-primary">
            <svg fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
              <path d="M36.7273 44C33.9891 44 31.6043 39.8386 30.3636 33.69C29.123 39.8386 26.7382 44 24 44C21.2618 44 18.877 39.8386 17.6364 33.69C16.3957 39.8386 14.0109 44 11.2727 44C7.25611 44 4 35.0457 4 24C4 12.9543 7.25611 4 11.2727 4C14.0109 4 16.3957 8.16144 17.6364 14.31C18.877 8.16144 21.2618 4 24 4C26.7382 4 29.123 8.16144 30.3636 14.31C31.6043 8.16144 33.9891 4 36.7273 4C40.7439 4 44 12.9543 44 24C44 35.0457 40.7439 44 36.7273 44Z" fill="currentColor"></path>
            </svg>
          </div>
          <h1 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">FlashAI</h1>
        </div>
        <div className="flex items-center gap-2 sm:gap-4">
          <button 
            onClick={onBack}
            className="text-xs sm:text-sm text-slate-600 hover:text-slate-800"
          >
            <span className="hidden sm:inline">← Back to Home</span>
            <span className="sm:hidden">← Back</span>
          </button>
          <div className="relative">
            <button className="size-10 rounded-full bg-cover bg-center" style={{backgroundImage: 'url("https://lh3.googleusercontent.com/aida-public/AB6AXuBTxwbGMqaUeFH9PrWgMLbzpSx6m3rPNt0AlMHqoejgByH4CpQNi2pRgNOpUQLiWknIG3Gnu7Vi26Fh9xpopFg33xGgOJTruu3ou-jUd5WLO1ESR6QjC88TX_UaHyoNgPmLM5r-jBrxsX73Mhdr9xa2vQeP7mMf3XcpabJoBe_6t0WyGB-lhx4pPrYUR_AEBFnDjiptz24Ve81j1Eyt3LqSJSULhb-FcrJKZJFJbgi_b-mRPsp0RSazn5F-K7V5y_WgpHnMjEjuTBY")'}}></button>
          </div>
        </div>
      </header>
      
      <main className="flex flex-1 items-center justify-center py-6 sm:py-12 bg-gray-50 relative">
        {/* Animated gradient background */}
        <div className="absolute inset-0 animated-gradient opacity-5"></div>
        <div className="w-full max-w-md px-4 relative z-10">
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Connect to your Database</h2>
            <p className="mt-2 text-sm sm:text-base text-slate-600">
              Enter your database credentials to get started.
            </p>
          </div>
          
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}
          
          <div className="mt-6 sm:mt-8 space-y-6">
            {/* Connector Option - First */}
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700" htmlFor="connectorId">Connector ID</label>
                <div className="mt-1">
                  <input 
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all duration-300 shadow-sm hover:shadow-md" 
                    id="connectorId" 
                    name="connectorId" 
                    placeholder="e.g., connector_abc123" 
                    type="text"
                    value={connectorId}
                    onChange={(e) => setConnectorId(e.target.value)}
                  />
                </div>
              </div>
              
              <button 
                className="w-full flex justify-center py-2.5 sm:py-2 px-3 sm:px-4 border border-blue-300 rounded-lg shadow-sm text-xs sm:text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50" 
                type="button"
                onClick={handleConnectWithConnector}
                disabled={isConnecting}
              >
                <span className="hidden sm:inline">{isConnecting ? 'Connecting...' : 'Connect with Connector ID'}</span>
                <span className="sm:hidden">{isConnecting ? 'Connecting...' : 'Connect with Connector'}</span>
              </button>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-xs sm:text-sm">
                <span className="px-2 bg-gray-50 text-gray-500">or</span>
              </div>
            </div>

            {/* Demo Data Option - Second */}
            <button 
              className="w-full flex justify-center py-2.5 sm:py-2 px-3 sm:px-4 border border-gray-300 rounded-lg shadow-sm text-xs sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50" 
              type="button"
              onClick={handleConnectWithEnv}
              disabled={isConnecting}
            >
              <span className="hidden sm:inline">{isConnecting ? 'Connecting...' : 'Connect with Demo Data'}</span>
              <span className="sm:hidden">{isConnecting ? 'Connecting...' : 'Connect with Demo Data'}</span>
            </button>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-xs sm:text-sm">
                <span className="px-2 bg-gray-50 text-gray-500">or</span>
              </div>
            </div>

            {/* Database Credentials - Third */}
            <form className="space-y-4 sm:space-y-6" onSubmit={handleSubmit}>
              <div>
                <label className="block text-sm font-medium text-slate-700" htmlFor="server">Host</label>
                <div className="mt-1">
                  <input 
                    autoComplete="host" 
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all duration-300 shadow-sm hover:shadow-md" 
                    id="server" 
                    name="server" 
                    placeholder="e.g., localhost" 
                    required 
                    type="text"
                    value={formData.server}
                    onChange={handleChange}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700" htmlFor="database">DB Name</label>
                <div className="mt-1">
                  <input 
                    autoComplete="db-name" 
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50" 
                    id="database" 
                    name="database" 
                    placeholder="e.g., my_database" 
                    required 
                    type="text"
                    value={formData.database}
                    onChange={handleChange}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700" htmlFor="username">Username</label>
                <div className="mt-1">
                  <input 
                    autoComplete="username" 
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50" 
                    id="username" 
                    name="username" 
                    placeholder="e.g., admin" 
                    required 
                    type="text"
                    value={formData.username}
                    onChange={handleChange}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700" htmlFor="password">Password</label>
                <div className="mt-1">
                  <input 
                    autoComplete="current-password" 
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all duration-300 shadow-sm hover:shadow-md" 
                    id="password" 
                    name="password" 
                    required 
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                  />
                </div>
              </div>
              
              <button 
                className="w-full flex justify-center py-2.5 sm:py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white btn-gradient hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 transition-all duration-300" 
                type="submit"
                disabled={isConnecting}
              >
                {isConnecting ? 'Connecting...' : 'Connect'}
              </button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DatabaseConnection;
